import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import { Button } from '../components/Button';
import './VoiceButton.css';

/**
 * The one voice control of the room: "turn voice on" first (a user gesture is required to
 * use the microphone and play sound), then a mute switch, or what is going on with the mic
 * (the line below says why, or what to do).
 */
export function VoiceButton() {
  const { voice, toasts } = useApp();
  if (!voice.enabled.value) {
    return (
      <Button
        variant="purple"
        block
        icon="🎙️"
        // enable() must run inside the tap handler itself: iOS only grants the microphone
        // and audio playback during a user gesture.
        onClick={() => {
          void voice.enable();
          // Speakers next to a microphone echo; most of it is cancelled, headphones fix it all.
          toasts.show(ru.voice.headphonesTip);
        }}
      >
        {ru.voice.enable}
      </Button>
    );
  }

  const permission = voice.micPermission.value;
  // A tap asks for the microphone again (inside a gesture, as browsers want it): it may have
  // been busy, unplugged, lost in the background, or allowed in the settings meanwhile.
  const retry = () => void voice.enable();
  if (voice.micRequesting.value) {
    return <VoiceNote icon="🎙️" text={ru.voice.requesting} hint={ru.voice.requestingHint} />;
  }
  if (permission === 'denied') {
    return (
      <VoiceNote icon="🚫" text={ru.voice.denied} hint={ru.voice.deniedHint} onRetry={retry} />
    );
  }
  if (permission === 'unsupported') {
    return <VoiceNote icon="🚫" text={ru.voice.unavailable} hint={ru.voice.unsupported} />;
  }
  if (!voice.micAvailable.value) {
    return (
      <VoiceNote
        icon="🚫"
        text={ru.voice.unavailable}
        hint={ru.voice.unavailableHint}
        onRetry={retry}
      />
    );
  }

  const muted = voice.muted.value;
  // The name says what a tap does; aria-pressed on top would contradict it.
  return (
    <Button
      variant={muted ? 'red' : 'green'}
      block
      icon={muted ? '🔇' : '🎤'}
      aria-label={muted ? ru.voice.unmute : ru.voice.mute}
      onClick={() => voice.toggleMute()}
    >
      {muted ? ru.voice.micOff : ru.voice.micOn}
    </Button>
  );
}

interface VoiceNoteProps {
  icon: string;
  text: string;
  hint: string;
  /** Makes the note a button that tries again. */
  onRetry?: () => void;
}

function VoiceNote({ icon, text, hint, onRetry }: VoiceNoteProps) {
  const body = (
    <>
      <span class="voice-note__text">
        <span aria-hidden="true">{icon}</span> {text}
      </span>
      <span class="voice-note__hint">{hint}</span>
    </>
  );
  if (!onRetry) {
    return (
      <div class="voice-note" role="status">
        {body}
      </div>
    );
  }
  return (
    <button type="button" class="voice-note voice-note--retry" onClick={onRetry}>
      {body}
      <span class="voice-note__retry">↻ {ru.voice.retry}</span>
    </button>
  );
}
